import React, { useState } from "react";

import {
	Badge,
	Button,
	ConfirmModal,
	InfoMessage,
	Input,
	SectionCard,
	Select,
	Spinner,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import MembersService from "@/services/MembersService";

import { usePlanGate } from "@/hooks/usePlanGate";

import { MemberAvatar } from "@/components/molecules/MemberAvatar/MemberAvatar";
import { PlanGate } from "@/components/organisms/PlanGate/PlanGate";

import {
	GRANTABLE_ROLES,
	ResourceRole,
	WORKSPACE_ROLES,
} from "@/constants/CRoles";

import { TGrantedRole, TMember, TMemberScope, TMemberShare } from "@/types";

import { errorToast, promiseToast } from "@/modules/index";

export interface IMembersCardProps {
	/** What the list belongs to: the workspace, a client (its projects follow) or one project. */
	scope: TMemberScope;
	id: number;
	/** The resource's owner or an admin of the workspace: may invite, re-role and remove. */
	canManage: boolean;
	currentUserId: number;
	className?: string;
}

/** A workspace grants its own roles (NONE included); a client or a project, a share. */
const rolesOf = (scope: TMemberScope): readonly TGrantedRole[] =>
	"workspaces" === scope ? WORKSPACE_ROLES : GRANTABLE_ROLES;

const roleOptions = (scope: TMemberScope) =>
	rolesOf(scope).map((role) => ({
		value: role,
		label: t(`roles.${role}`),
	}));

/** The exceptions unfolded under a member of the workspace. */
const Shares: React.FC<{ shares: TMemberShare[] }> = ({ shares }) => (
	<details className="mt-1" data-testid="member-shares">
		<summary className="cursor-pointer text-xs text-neutral-500">
			{t("members.shares", { count: shares.length })}
		</summary>
		<ul className="mt-1 flex flex-col gap-1">
			{shares.map((share) => (
				<li
					key={`${share.scope}-${share.id}`}
					className="flex items-center gap-2 text-xs text-neutral-600"
				>
					<Badge size="sm" variant="tint" color="neutral">
						{t(`members.scope.${share.scope}`)}
					</Badge>
					<Link
						to={`/${share.scope}/${share.id}`}
						className="truncate hover:underline"
					>
						{share.name}
					</Link>
					<span className="text-neutral-400">
						· {t(`roles.${share.role}`)}
					</span>
				</li>
			))}
		</ul>
	</details>
);

/**
 * Who works on this client or this project. The owner comes first and
 * is fixed; every other row is a share whose role a manager can change.
 * Anyone can remove themselves (leave); managers remove anyone but the
 * owner. Inviting is by the mail of an existing account, and it also
 * lets that account into the workspace - one gesture (docs/modele.md).
 */
export const MembersCard: React.FC<IMembersCardProps> = ({
	scope,
	id,
	canManage,
	currentUserId,
	className,
}) => {
	// Inviting is a plan feature: off-plan the form stays, its button
	// locks and opens the upgrade modal (the API would answer 402 anyway).
	const sharing = usePlanGate("sharing");
	const queryClient = useQueryClient();
	const [inviteMail, setInviteMail] = useState("");
	const [inviteRole, setInviteRole] = useState<TGrantedRole>(
		ResourceRole.VIEWER
	);
	const [removeTarget, setRemoveTarget] = useState<TMember | null>(null);

	const queryKey = [scope, id, "members"];
	const { data: members, isLoading } = useQuery({
		queryKey,
		queryFn: () => MembersService.list(scope, id),
	});

	// A new member may reach more than this list: the resource's own
	// queries (and the workspace's usage gauge) refresh too.
	const invalidate = () =>
		queryClient.invalidateQueries({
			predicate: (query) =>
				query.queryKey[0] === scope ||
				query.queryKey[0] === "subscription",
		});

	const addMutation = useMutation({
		mutationFn: () =>
			MembersService.add(scope, id, {
				mail: inviteMail.trim(),
				role: inviteRole,
			}),
		onSuccess: () => {
			setInviteMail("");
			invalidate();
		},
	});

	const roleMutation = useMutation({
		mutationFn: ({
			userId,
			role,
		}: {
			userId: number;
			role: TGrantedRole;
		}) => MembersService.update(scope, id, userId, { role }),
		onSuccess: invalidate,
	});

	const removeMutation = useMutation({
		mutationFn: (userId: number) =>
			MembersService.remove(scope, id, userId),
		onSuccess: invalidate,
	});

	const handleInvite = (event: React.FormEvent) => {
		event.preventDefault();
		const mail = inviteMail.trim();
		if (!mail) return;
		// No account behind the address: the API mailed an invitation
		// (202) and there is no member to list yet - say so.
		const prom = addMutation.mutateAsync().then((result) => {
			if ("invited" in result) {
				toast.info(t("members.invited", { mail: result.mail }));
			} else {
				toast.success(t("promiseToast.success"));
			}
		});
		void errorToast(prom);
	};

	const others = (members ?? []).filter((member) => !member.isOwner);

	return (
		<SectionCard
			data-testid="members-card"
			title={
				<>
					{t("members.title")}
					{members && (
						<span className="ml-1.5 font-normal text-neutral-500">
							· {members.length}
						</span>
					)}
				</>
			}
			className={className}
		>
			{"clients" === scope && others.length > 0 && (
				<InfoMessage color="info" className="mb-3">
					{t("members.clientCascade")}
				</InfoMessage>
			)}
			{isLoading && <Spinner className="mx-auto my-4" />}
			<ul className="flex flex-col divide-y divide-neutral-100">
				{(members ?? []).map((member) => {
					const isSelf = member.userId === currentUserId;
					const canRemove = !member.isOwner && (canManage || isSelf);
					return (
						<li
							key={member.userId}
							className="flex items-center gap-3 py-2.5"
							data-testid={`member-${member.userId}`}
						>
							<MemberAvatar member={member} />
							<span className="flex min-w-0 grow flex-col">
								<span className="truncate text-sm font-medium">
									{member.firstName} {member.lastName}
									{isSelf && (
										<span className="ml-1 font-normal text-neutral-500">
											· {t("members.you")}
										</span>
									)}
								</span>
								<span className="truncate text-xs text-neutral-500">
									{member.mail}
								</span>
								{!!member.shares?.length && (
									<Shares shares={member.shares} />
								)}
							</span>
							{member.isOwner || !canManage ? (
								<Badge
									size="sm"
									variant="tint"
									color={
										member.isOwner ? "primary" : "neutral"
									}
								>
									{t(`roles.${member.role}`)}
								</Badge>
							) : (
								<Select<TGrantedRole>
									size="sm"
									options={roleOptions(scope)}
									value={member.role as TGrantedRole}
									onChange={(role) =>
										role &&
										!Array.isArray(role) &&
										promiseToast(
											roleMutation.mutateAsync({
												userId: member.userId,
												role,
											})
										)
									}
									placeholder={t("members.role")}
									hideError
								/>
							)}
							{canRemove && (
								<Button
									size="sm"
									variant="ghost"
									picto="cross"
									title={
										isSelf
											? t("members.leave")
											: t("members.remove")
									}
									aria-label={
										isSelf
											? t("members.leave")
											: t("members.remove")
									}
									onClick={() => setRemoveTarget(member)}
								/>
							)}
						</li>
					);
				})}
			</ul>
			{!isLoading && others.length === 0 && (
				<p className="py-2 text-sm text-neutral-500">
					{t("members.empty")}
				</p>
			)}
			{canManage && (
				<form
					onSubmit={handleInvite}
					className="mt-3 flex flex-col gap-2 border-t border-neutral-100 pt-3 sm:flex-row sm:items-end"
					data-testid="invite-form"
				>
					<Input
						type="email"
						label={t("members.inviteMail")}
						value={inviteMail}
						onChange={setInviteMail}
						placeholder="nom@exemple.fr"
						wrapperClassName="grow"
						hideError
					/>
					<Select<TGrantedRole>
						label={t("members.role")}
						options={roleOptions(scope)}
						value={inviteRole}
						onChange={(role) =>
							role && !Array.isArray(role) && setInviteRole(role)
						}
						hideError
						wrapperClassName="grow"
					/>
					<PlanGate on="sharing">
						<Button
							type={sharing.locked ? "button" : "submit"}
							color="primary"
							picto="userPlus"
							isLoading={addMutation.isPending}
							disabled={!inviteMail.trim()}
						>
							{t("members.invite")}
						</Button>
					</PlanGate>
				</form>
			)}
			<ConfirmModal
				open={!!removeTarget}
				onClose={() => setRemoveTarget(null)}
				size="sm"
				title={t("members.confirmRemove.title")}
				onConfirm={() => {
					promiseToast(
						removeMutation.mutateAsync(removeTarget!.userId)
					);
					setRemoveTarget(null);
				}}
				confirmText={
					removeTarget?.userId === currentUserId
						? t("members.leave")
						: t("members.remove")
				}
				confirmColor="danger"
				cancelText={t("actions.cancel")}
			>
				<Trans
					i18nKey="members.confirmRemove.message"
					values={{
						name: `${removeTarget?.firstName ?? ""} ${removeTarget?.lastName ?? ""}`.trim(),
					}}
					components={{ b: <b /> }}
				/>
			</ConfirmModal>
		</SectionCard>
	);
};
